import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Button,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { NumberBox } from "devextreme-react";
import TextArea from "devextreme-react/text-area";
import CustomSelectBox from "../../common/CustomSelectBox";
import closeIcon from "../../../images/closeIcon.svg";
import { baseRequest } from "../../../fetch/basicThunk";
import { QUERY_PUT_BILLING_PAYMENT_ORDER_APPROVAL } from "../../../fetch/constantQueries";
import { openModal } from "../../../redux/modal/modalSlice";

export default function ApprovePaymentOrderModal({
  open,
  order,
  onClose,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [approvalMode, setApprovalMode] = useState(null);
  const [doNotChargeReason, setDoNotChargeReason] = useState("");
  const [discountReason, setDiscountReason] = useState(null);
  const [discountValue, setDiscountValue] = useState(null);
  const [discountComment, setDiscountComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const getRubricLabel = useCallback(
    (code) => {
      switch (code) {
        case "BASE_RATE":
          return t("BillingPaymentOrders.RubricBaseRate");
        case "EXCESS_KM":
          return t("BillingPaymentOrders.RubricExcessKm");
        case "TOLL":
          return t("BillingPaymentOrders.RubricToll");
        case "STOP_HOUR":
          return t("BillingPaymentOrders.RubricStopHour");
        case "SKATES":
          return t("BillingPaymentOrders.RubricSkates");
        case "DISPATCH_FEE":
          return t("BillingPaymentOrders.RubricDispatchFee");
        case "OTHER":
          return t("BillingPaymentOrders.RubricOther");
        default:
          return code || "";
      }
    },
    [t]
  );

  useEffect(() => {
    if (open && order) {
      if (order.doNotChargeClient) {
        setApprovalMode("DO_NOT_CHARGE");
      } else if (order.discountValue && Number(order.discountValue) > 0) {
        setApprovalMode("INSERT_DISCOUNT");
      } else {
        setApprovalMode(null);
      }
      setDoNotChargeReason(order.doNotChargeReason || "");
      setDiscountReason(order.discountReason || null);
      setDiscountValue(order.discountValue ? Number(order.discountValue) : null);
      setDiscountComment(order.discountComment || "");
      setErrorMessage("");
    } else {
      setApprovalMode(null);
      setDoNotChargeReason("");
      setDiscountReason(null);
      setDiscountValue(null);
      setDiscountComment("");
      setErrorMessage("");
    }
  }, [open, order]);

  const approvalModeOptions = useMemo(
    () => [
      { id: "DO_NOT_CHARGE", text: t("BillingPaymentOrders.ApproveModalDoNotCharge") },
      { id: "INSERT_DISCOUNT", text: t("BillingPaymentOrders.ApproveModalInsertDiscount") },
    ],
    [t]
  );

  const discountReasonOptions = useMemo(
    () => [
      { id: "COMMERCIAL_AGREEMENT", text: t("BillingPaymentOrders.DiscountReasonCommercial") },
      { id: "SERVICE_DELAY", text: t("BillingPaymentOrders.DiscountReasonDelay") },
      { id: "OPERATIONAL_FAILURE", text: t("BillingPaymentOrders.DiscountReasonOperational") },
      { id: "COURTESY", text: t("BillingPaymentOrders.DiscountReasonCourtesy") },
      { id: "OTHER", text: t("BillingPaymentOrders.DiscountReasonOther") },
    ],
    [t]
  );

  const unitPrice = Number(order?.unitPrice) || 0;
  const quantity = Number(order?.quantity) || 1;
  const totalValue = Number(order?.totalValue) || (unitPrice * quantity);

  const opName =
    getRubricLabel(order?.rubricType) ||
    order?.description ||
    t("BillingPaymentOrders.RubricOther");

  const formatCurrency = (val) =>
    `R$ ${Number(val || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const calculatedSalePrice = useMemo(() => {
    if (approvalMode === "DO_NOT_CHARGE") {
      return 0;
    }
    if (approvalMode === "INSERT_DISCOUNT" && discountValue > 0) {
      return Math.max(0, totalValue - Number(discountValue));
    }
    return totalValue;
  }, [approvalMode, discountValue, totalValue]);

  const handleToggleOption = (mode) => {
    if (approvalMode === mode) {
      setApprovalMode(null);
      setDoNotChargeReason("");
      setDiscountReason(null);
      setDiscountValue(null);
      setDiscountComment("");
      setErrorMessage("");
    } else {
      setApprovalMode(mode);
      setErrorMessage("");
      if (mode === "DO_NOT_CHARGE") {
        setDiscountReason(null);
        setDiscountValue(null);
        setDiscountComment("");
      } else if (mode === "INSERT_DISCOUNT") {
        setDoNotChargeReason("");
      }
    }
  };

  const handleClearSelection = () => {
    setApprovalMode(null);
    setDoNotChargeReason("");
    setDiscountReason(null);
    setDiscountValue(null);
    setDiscountComment("");
    setErrorMessage("");
  };

  const isFormValid = useMemo(() => {
    if (approvalMode === "DO_NOT_CHARGE") {
      return Boolean(doNotChargeReason && doNotChargeReason.trim().length > 0);
    }
    if (approvalMode === "INSERT_DISCOUNT") {
      const numDiscount = Number(discountValue);
      return (
        Boolean(discountReason) &&
        !isNaN(numDiscount) &&
        numDiscount > 0 &&
        numDiscount <= totalValue
      );
    }
    return true;
  }, [approvalMode, doNotChargeReason, discountReason, discountValue, totalValue]);

  const handleSubmit = async () => {
    if (approvalMode === "DO_NOT_CHARGE" && (!doNotChargeReason || !doNotChargeReason.trim())) {
      setErrorMessage(t("BillingPaymentOrders.ErrorApproveRequiredJustification"));
      return;
    }

    if (approvalMode === "INSERT_DISCOUNT") {
      if (!discountReason) {
        setErrorMessage(t("BillingPaymentOrders.ErrorApproveRequiredDiscountReason"));
        return;
      }
      const numDiscount = Number(discountValue);
      if (isNaN(numDiscount) || numDiscount <= 0 || numDiscount > totalValue) {
        setErrorMessage(t("BillingPaymentOrders.ErrorApproveInvalidDiscountValue"));
        return;
      }
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const isDoNotCharge = approvalMode === "DO_NOT_CHARGE";
      const isInsertDiscount = approvalMode === "INSERT_DISCOUNT";

      const payload = {
        status: "APPROVED",
        doNotChargeClient: isDoNotCharge,
        doNotChargeReason: isDoNotCharge ? doNotChargeReason.trim() : null,
        discountReason: isInsertDiscount ? discountReason : null,
        discountValue: isInsertDiscount ? Number(discountValue) : 0,
        discountComment: isInsertDiscount && discountComment ? discountComment.trim() : null,
      };

      await baseRequest(
        { param: `${order.id}/approval`, body: payload },
        dispatch,
        QUERY_PUT_BILLING_PAYMENT_ORDER_APPROVAL,
        "PUT"
      );

      setLoading(false);
      dispatch(
        openModal({
          open: true,
          title: t("BillingPaymentOrders.SuccessApprove"),
          type: "success",
        })
      );
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setLoading(false);
      dispatch(
        openModal({
          open: true,
          title: t("BillingPaymentOrders.ErrorApproveOrder"),
          type: "error",
        })
      );
    }
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ square: true, style: { borderRadius: 0 } }}
    >
      <div style={{ backgroundColor: "#ffffff" }}>
        <DialogTitle
          disableTypography
          style={{
            padding: "1rem 1.5rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: "1.125rem",
              color: "#000000",
            }}
          >
            {t("BillingPaymentOrders.ApproveModalTitle")}
          </span>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={loading}
            aria-label="close"
          >
            <img src={closeIcon} alt="close" style={{ width: "1rem", height: "1rem" }} />
          </IconButton>
        </DialogTitle>

        <Divider style={{ backgroundColor: "#000000" }} orientation="horizontal" />

        <DialogContent style={{ padding: "1.5rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div>
              <Typography variant="body1" style={{ color: "#333333" }}>
                {t("BillingPaymentOrders.ApproveModalOpNotice")}{" "}
                <span
                  style={{
                    backgroundColor: "#e0e2e5",
                    color: "#000000",
                    borderRadius: "12px",
                    padding: "0.2rem 0.6rem",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    display: "inline-block",
                  }}
                >
                  {opName}
                </span>
              </Typography>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                backgroundColor: "transparent",
                marginBottom: "1rem",
              }}
            >
              <Typography variant="body2" style={{ color: "#000000" }}>
                <strong style={{ fontWeight: 700 }}>
                  {t("BillingPaymentOrders.ApproveModalUnitPrice")}{" "}
                </strong>
                <span style={{ fontWeight: 400 }}>{formatCurrency(unitPrice)}</span>
              </Typography>
              <Typography variant="body2" style={{ color: "#000000" }}>
                <strong style={{ fontWeight: 700 }}>
                  {t("BillingPaymentOrders.ApproveModalTotalValue")}{" "}
                </strong>
                <span style={{ fontWeight: 400 }}>{formatCurrency(totalValue)}</span>
              </Typography>
            </div>

            <Divider style={{ backgroundColor: "#e0e0e0", marginBottom: "0.5rem" }} orientation="horizontal" />

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "0.75rem",
                }}
              >
                <div
                  role="radiogroup"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  {approvalModeOptions.map((opt) => {
                    const isSelected = approvalMode === opt.id;
                    return (
                      <div
                        key={opt.id}
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onClick={() => {
                          if (!loading) {
                            handleToggleOption(opt.id);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (!loading && (e.key === " " || e.key === "Enter")) {
                            e.preventDefault();
                            handleToggleOption(opt.id);
                          }
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.625rem",
                          cursor: loading ? "default" : "pointer",
                          userSelect: "none",
                        }}
                      >
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            boxSizing: "border-box",
                            backgroundColor: isSelected ? "transparent" : "#71767C",
                            border: isSelected ? "6px solid #007BC0" : "none",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            color: "#000000",
                            fontSize: "0.9375rem",
                            fontWeight: 500,
                          }}
                        >
                          {opt.text}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {approvalMode && (
                  <Button
                    size="small"
                    onClick={handleClearSelection}
                    style={{
                      textTransform: "none",
                      color: "#007BC0",
                      padding: "2px 8px",
                      fontSize: "0.8125rem",
                      borderRadius: 0,
                    }}
                    disabled={loading}
                  >
                    {t("BillingPaymentOrders.ApproveModalClearSelection")}
                  </Button>
                )}
              </div>

              {approvalMode === "DO_NOT_CHARGE" && (
                <div style={{ marginTop: "0.25rem" }}>
                  <TextArea
                    label={t("BillingPaymentOrders.ApproveModalDoNotChargeReason")}
                    labelMode="floating"
                    placeholder={t("BillingPaymentOrders.ApproveModalDoNotChargeReasonPlaceholder")}
                    value={doNotChargeReason}
                    valueChangeEvent="change"
                    height={80}
                    autoResizeEnabled={false}
                    onValueChanged={(e) => {
                      setDoNotChargeReason(e.value || "");
                      if (errorMessage) setErrorMessage("");
                    }}
                    disabled={loading}
                  />
                </div>
              )}

              {approvalMode === "INSERT_DISCOUNT" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    marginTop: "0.25rem",
                  }}
                >
                  <CustomSelectBox
                    label={t("BillingPaymentOrders.ApproveModalDiscountReason")}
                    dataSource={discountReasonOptions}
                    valueExpr="id"
                    displayExpr="text"
                    value={discountReason}
                    onValueChanged={(e) => {
                      setDiscountReason(e.value);
                      if (errorMessage) setErrorMessage("");
                    }}
                    disabled={loading}
                  />

                  <NumberBox
                    label={t("BillingPaymentOrders.ApproveModalDiscountValue")}
                    labelMode="floating"
                    value={discountValue}
                    format="R$ #,##0.00"
                    min={0.01}
                    max={totalValue}
                    step={0.01}
                    onValueChanged={(e) => {
                      setDiscountValue(e.value);
                      if (errorMessage) setErrorMessage("");
                    }}
                    disabled={loading}
                  />

                  <TextArea
                    label={t("BillingPaymentOrders.ApproveModalDiscountComment")}
                    labelMode="floating"
                    placeholder={t("BillingPaymentOrders.ApproveModalDiscountCommentPlaceholder")}
                    value={discountComment}
                    valueChangeEvent="change"
                    height={80}
                    autoResizeEnabled={false}
                    onValueChanged={(e) => setDiscountComment(e.value || "")}
                    disabled={loading}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: "#f4f4f4",
                      padding: "8px 12px",
                      borderRadius: 0,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 500, color: "#555555" }}>
                      {t("BillingPaymentOrders.ApproveModalSalePricePreview")}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 700, color: "#007BC0" }}>
                      {formatCurrency(calculatedSalePrice)}
                    </Typography>
                  </div>
                </div>
              )}
            </div>

            {errorMessage && (
              <Typography variant="body2" style={{ color: "#D50005", fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            )}
          </div>
        </DialogContent>

        <DialogActions
          style={{
            padding: "1rem 1.5rem 1.5rem 1.5rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={!isFormValid || loading}
            style={{ borderRadius: 0 }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("BillingPaymentOrders.ApproveModalSubmit")
            )}
          </Button>
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            disabled={loading}
            style={{ borderRadius: 0 }}
          >
            {t("BillingPaymentOrders.ApproveModalCancel")}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
